import React from 'react';
import { motion } from 'framer-motion';
import { ReliabilityScore } from '../../types';
import { TrendingUp, Award, Clock, DollarSign } from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, XAxis, Tooltip } from 'recharts';
interface RentReliabilityScoreProps {
  scoreData: ReliabilityScore;
}
export function RentReliabilityScore({
  scoreData
}: RentReliabilityScoreProps) {
  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-blue-600';
    if (score >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };
  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'Excellent':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Good':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Fair':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  return <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-2817">
      <div className="flex justify-between items-start mb-6" data-id="element-2818">
        <div data-id="element-2819">
          <h3 className="text-lg font-bold text-gray-900 flex items-center" data-id="element-2820">
            <Award className="h-5 w-5 text-blue-600 mr-2" data-id="element-2821" />
            Rent Reliability Score
          </h3>
          <p className="text-sm text-gray-500 mt-1" data-id="element-2822">
            Your rental creditworthiness
          </p>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide border ${getTierColor(scoreData.tier)}`} data-id="element-2823">
          {scoreData.tier}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center mb-8" data-id="element-2824">
        <div className="text-center relative" data-id="element-2825">
          <svg className="w-48 h-48 mx-auto transform -rotate-90" data-id="element-2826">
            <circle className="text-gray-100" strokeWidth="16" stroke="currentColor" fill="transparent" r="80" cx="96" cy="96" data-id="element-2827" />
            <motion.circle className={getScoreColor(scoreData.score)} strokeWidth="16" strokeDasharray={502} strokeDashoffset={502 - 502 * scoreData.score / 100} strokeLinecap="round" stroke="currentColor" fill="transparent" r="80" cx="96" cy="96" initial={{
            strokeDashoffset: 502
          }} animate={{
            strokeDashoffset: 502 - 502 * scoreData.score / 100
          }} transition={{
            duration: 1.5,
            ease: 'easeOut'
          }} data-id="element-2828" />
          </svg>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" data-id="element-2829">
            <span className={`text-5xl font-bold ${getScoreColor(scoreData.score)}`} data-id="element-2830">
              {scoreData.score}
            </span>
            <span className="block text-xs text-gray-400 font-medium uppercase mt-1" data-id="element-2831">
              out of 100
            </span>
          </div>
        </div>

        <div className="space-y-4" data-id="element-2832">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-id="element-2833">
            <div className="flex items-center" data-id="element-2834">
              <Clock className="h-5 w-5 text-blue-500 mr-3" data-id="element-2835" />
              <span className="text-sm font-medium text-gray-700" data-id="element-2836">
                On-Time Payments
              </span>
            </div>
            <span className="font-bold text-gray-900" data-id="element-2837">
              {scoreData.metrics.onTimePercentage}%
            </span>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-id="element-2838">
            <div className="flex items-center" data-id="element-2839">
              <TrendingUp className="h-5 w-5 text-green-500 mr-3" data-id="element-2840" />
              <span className="text-sm font-medium text-gray-700" data-id="element-2841">
                Current Streak
              </span>
            </div>
            <span className="font-bold text-gray-900" data-id="element-2842">
              {scoreData.metrics.currentStreak} Months
            </span>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-id="element-2843">
            <div className="flex items-center" data-id="element-2844">
              <DollarSign className="h-5 w-5 text-purple-500 mr-3" data-id="element-2845" />
              <span className="text-sm font-medium text-gray-700" data-id="element-2846">
                Total Rent Paid
              </span>
            </div>
            <span className="font-bold text-gray-900" data-id="element-2847">
              ₦{(scoreData.metrics.totalPaid / 1000000).toFixed(1)}M
            </span>
          </div>
        </div>
      </div>

      <div className="h-32 w-full" data-id="element-2848">
        <ResponsiveContainer width="100%" height="100%" data-id="element-2849">
          <AreaChart data={scoreData.history} data-id="element-2850">
            <defs data-id="element-2851">
              <linearGradient id="colorScore" x1="0" y1="0" x2="0" y2="1" data-id="element-2852">
                <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.1} data-id="element-2853" />
                <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} data-id="element-2854" />
              </linearGradient>
            </defs>
            <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
            fontSize: 10
          }} data-id="element-2855" />
            <Tooltip data-id="element-2856" />
            <Area type="monotone" dataKey="score" stroke="#3B82F6" fillOpacity={1} fill="url(#colorScore)" strokeWidth={2} data-id="element-2857" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>;
}